import React, { useState } from 'react';
import { Button } from '../ui/button';
import {
  FileText, Download, Printer, Shield, CreditCard,
  AlertCircle, ChevronRight, Folder, CheckSquare, Loader2, PenTool,
} from 'lucide-react';
import SignatureElectroniqueModal from './SignatureElectroniqueModal';

export default function ResultatDocuments({ savedSimulation, identite, downloadingDoc, handleDownloadDocument, handlePrintDocumentsList }) {
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const isSigned = !!savedSimulation?.signature_electronique?.signed
    || !!savedSimulation?.devis_signed_at;
  const signataireDefaultNom = (
    `${identite?.prenom || ''} ${identite?.nom || ''}`.trim()
    || identite?.raison_sociale
    || ''
  );
  const handleSigned = () => {
    // Le devis est signé : forcer le rafraîchissement de l'objet simulation
    // pour que le titre PDF bascule en "DOCUMENT VALANT FACTURE" au prochain
    // download. On modifie l'objet local (mutation lazy autorisée car le parent
    // re-fetch via PaiementValidation après upgrade plan).
    if (savedSimulation) {
      savedSimulation.signature_electronique = { signed: true };
      savedSimulation.devis_signed_at = new Date().toISOString();
    }
  };
  return (
    <div className="space-y-3">
      <h4 className="font-semibold text-gray-700 flex items-center gap-2">
        <Download className="h-4 w-4" />
        Telecharger les documents
      </h4>
      <div className="space-y-2">
        <button
          onClick={() => handleDownloadDocument(savedSimulation.reference, 'dossier-maitre', `Dossier_Maitre_DOMIUM_${savedSimulation.reference}.pdf`)}
          disabled={!!downloadingDoc}
          className="w-full flex items-center gap-3 p-3 rounded-lg border-2 border-blue-200 bg-blue-50/50 hover:bg-blue-100 hover:border-blue-300 transition-all text-left group disabled:opacity-60"
          data-testid="btn-dossier-maitre"
        >
          <div className="p-2 rounded-lg bg-blue-100 text-blue-600 group-hover:bg-blue-200 transition-colors">
            <FileText className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-blue-800">DOSSIER MAITRE — CP + CG + NOTE</div>
            <div className="text-xs text-blue-500">Conditions Particulieres + Generales + Note technique</div>
          </div>
          {downloadingDoc === 'dossier-maitre' ? <Loader2 className="h-4 w-4 text-blue-500 animate-spin flex-shrink-0" /> : <Download className="h-4 w-4 text-blue-400 group-hover:text-blue-600 transition-colors flex-shrink-0" />}
        </button>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => handleDownloadDocument(savedSimulation.reference, 'devis-ess', `Devis_ESS_DOMIUM_${savedSimulation.reference}.pdf`)}
            disabled={!!downloadingDoc}
            className="flex items-center gap-3 p-3 rounded-lg border-2 border-slate-200 bg-slate-50/50 hover:bg-slate-100 hover:border-slate-300 transition-all text-left group disabled:opacity-60"
            data-testid="btn-devis-ess"
          >
            <div className="p-2 rounded-lg bg-slate-100 text-slate-600 group-hover:bg-slate-200 transition-colors">
              <CreditCard className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-700">DEVIS ESS</div>
              <div className="text-xs text-slate-400">DOS DOMIUM</div>
            </div>
            {downloadingDoc === 'devis-ess' ? <Loader2 className="h-4 w-4 text-slate-400 animate-spin flex-shrink-0" /> : <Download className="h-4 w-4 text-slate-300 group-hover:text-slate-500 transition-colors flex-shrink-0" />}
          </button>
          <button
            onClick={() => handleDownloadDocument(savedSimulation.reference, 'attestation-draft', `Attestation_DRAFT_DOMIUM_${savedSimulation.reference}.pdf`)}
            disabled={!!downloadingDoc}
            className="flex items-center gap-3 p-3 rounded-lg border-2 border-amber-200 bg-amber-50/50 hover:bg-amber-100 hover:border-amber-300 transition-all text-left group disabled:opacity-60"
            data-testid="btn-attestation-draft"
          >
            <div className="p-2 rounded-lg bg-amber-100 text-amber-600 group-hover:bg-amber-200 transition-colors">
              <FileText className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-amber-700">ATTESTATION DRAFT</div>
              <div className="text-xs text-amber-400">DOMIUM</div>
            </div>
            {downloadingDoc === 'attestation-draft' ? <Loader2 className="h-4 w-4 text-amber-400 animate-spin flex-shrink-0" /> : <Download className="h-4 w-4 text-amber-300 group-hover:text-amber-500 transition-colors flex-shrink-0" />}
          </button>
        </div>
        {(isSigned
          || savedSimulation?.signature?.statut === 'signed'
          || savedSimulation?.paiement_valide
          || savedSimulation?.recu_acquitte) && (
          <button
            onClick={() => handleDownloadDocument(savedSimulation.reference, 'attestation-nominative', `Attestation_Nominative_DOMIUM_${savedSimulation.reference}.pdf`)}
            disabled={!!downloadingDoc}
            className="w-full flex items-center gap-3 p-3 rounded-lg border-2 border-emerald-300 bg-emerald-50/60 hover:bg-emerald-100 hover:border-emerald-400 transition-all text-left group disabled:opacity-60"
            data-testid="btn-attestation-nominative"
          >
            <div className="p-2 rounded-lg bg-emerald-100 text-emerald-700 group-hover:bg-emerald-200 transition-colors">
              <Shield className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-emerald-800">ATTESTATION NOMINATIVE</div>
              <div className="text-xs text-emerald-600">Officielle — QR de vérification · intègre la signature électronique du Maître d'ouvrage si signée</div>
            </div>
            {downloadingDoc === 'attestation-nominative' ? <Loader2 className="h-4 w-4 text-emerald-500 animate-spin flex-shrink-0" /> : <Download className="h-4 w-4 text-emerald-400 group-hover:text-emerald-600 transition-colors flex-shrink-0" />}
          </button>
        )}
        {identite.qualite !== 'particulier' && (
          <button
            onClick={() => handleDownloadDocument(savedSimulation.reference, 'attestation-ftpe-draft', `Attestation_FTPE_DRAFT_${savedSimulation.reference}.pdf`)}
            disabled={!!downloadingDoc}
            className="w-full flex items-center gap-3 p-3 rounded-lg border-2 border-purple-200 bg-purple-50/50 hover:bg-purple-100 hover:border-purple-300 transition-all text-left group disabled:opacity-60"
            data-testid="btn-attestation-ftpe-draft"
          >
            <div className="p-2 rounded-lg bg-purple-100 text-purple-600 group-hover:bg-purple-200 transition-colors">
              <Shield className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-purple-700">ATTESTATION FTPE DRAFT</div>
              <div className="text-xs text-purple-400">Partenaire professionnel</div>
            </div>
            {downloadingDoc === 'attestation-ftpe-draft' ? <Loader2 className="h-4 w-4 text-purple-400 animate-spin flex-shrink-0" /> : <Download className="h-4 w-4 text-purple-300 group-hover:text-purple-500 transition-colors flex-shrink-0" />}
          </button>
        )}
      </div>
      <Button
        onClick={() => handleDownloadDocument(savedSimulation.reference, 'pack', `Pack_DOMIUM_${savedSimulation.reference}.zip`)}
        disabled={!!downloadingDoc}
        className="w-full bg-emerald-600 hover:bg-emerald-700 h-11 disabled:opacity-60"
      >
        {downloadingDoc === 'pack' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
        {downloadingDoc === 'pack' ? 'Telechargement en cours...' : 'Telecharger tout (ZIP)'}
      </Button>

      {/* Bloc Signature électronique du Devis ESS */}
      <div className={`mt-3 rounded-lg border-2 p-4 transition-all ${isSigned ? 'border-emerald-300 bg-emerald-50/60' : 'border-blue-300 bg-gradient-to-br from-blue-50 to-indigo-50/60'}`}>
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-full flex-shrink-0 ${isSigned ? 'bg-emerald-200' : 'bg-blue-200'}`}>
            <PenTool className={`h-5 w-5 ${isSigned ? 'text-emerald-700' : 'text-blue-700'}`} />
          </div>
          <div className="flex-1 min-w-0">
            <h5 className={`font-bold mb-1 ${isSigned ? 'text-emerald-800' : 'text-blue-800'}`}>
              {isSigned ? '✓ Devis signé électroniquement' : 'Signer électroniquement le Devis ESS'}
            </h5>
            <p className={`text-xs mb-3 leading-relaxed ${isSigned ? 'text-emerald-700' : 'text-blue-700'}`}>
              {isSigned
                ? 'Le devis bascule automatiquement en « Document valant facture » au prochain téléchargement.'
                : 'Recevez un code par email et signez sans imprimer/scanner. Une fois signé, le devis vaut facture coopérative.'}
            </p>
            {!isSigned && (
              <Button
                onClick={() => setSignatureModalOpen(true)}
                size="sm"
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white"
                data-testid="btn-open-signature-modal"
              >
                <PenTool className="h-4 w-4 mr-2" />
                Signer maintenant
              </Button>
            )}
          </div>
        </div>
      </div>

      <SignatureElectroniqueModal
        open={signatureModalOpen}
        onClose={() => setSignatureModalOpen(false)}
        reference={savedSimulation?.reference}
        signataireDefaultNom={signataireDefaultNom}
        onSigned={handleSigned}
      />

      {/* Liste des documents a fournir */}
      <div className="mt-4 bg-slate-50 p-4 rounded-lg border border-slate-200">
        <div className="flex items-center justify-between mb-3">
          <h5 className="font-semibold text-slate-700 flex items-center gap-2">
            <Folder className="h-4 w-4" />
            Documents a fournir
          </h5>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePrintDocumentsList(savedSimulation.reference)}
            className="text-slate-600 hover:text-slate-800 hover:bg-slate-100"
          >
            <Printer className="h-4 w-4 mr-2" />
            Imprimer PDF
          </Button>
        </div>
        <ul className="space-y-2 text-sm text-slate-600">
          <li className="flex items-start gap-2"><CheckSquare className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" /><span>Permis de construire</span></li>
          <li className="flex items-start gap-2"><CheckSquare className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" /><span>Declaration d'ouverture de chantier</span></li>
          <li className="flex items-start gap-2"><CheckSquare className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" /><span>Plans d'architecture (coupes, distribution)</span></li>
          <li className="flex items-start gap-2"><CheckSquare className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" /><span>Plan de masse</span></li>
          <li className="flex items-start gap-2"><CheckSquare className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" /><span>Etudes techniques (Structure, etude de sol, etude d'assainissement, conformite...)</span></li>
          <li className="flex items-start gap-2"><CheckSquare className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" /><span>Attestation d'assurances des intervenants a la construction</span></li>
          <li className="flex items-start gap-2"><CheckSquare className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" /><span>Devis des intervenants a la construction</span></li>
          {identite.qualite !== 'particulier' && (
            <li className="flex items-start gap-2"><CheckSquare className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" /><span className="font-medium text-slate-800">Justificatif d'immatriculation de la societe</span></li>
          )}
        </ul>
      </div>

      {/* Bloc ATTENTION */}
      <div className="mt-3 bg-amber-50 p-4 rounded-lg border-2 border-amber-300">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-amber-200 rounded-full flex-shrink-0">
            <AlertCircle className="h-5 w-5 text-amber-700" />
          </div>
          <div>
            <h5 className="font-bold text-amber-800 mb-2">ATTENTION</h5>
            <p className="text-sm text-amber-700 mb-2">A la reception des travaux, transmettre :</p>
            <ul className="space-y-1 text-sm text-amber-700">
              <li className="flex items-center gap-2"><ChevronRight className="h-4 w-4 flex-shrink-0" /><span>PV de reception des travaux sans reserves</span></li>
              <li className="flex items-center gap-2"><ChevronRight className="h-4 w-4 flex-shrink-0" /><span>Le cas echeant, la DAACT (Declaration Attestant l'Achevement et la Conformite des Travaux)</span></li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
